import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { connectRoom } from '../../../actions/users';
import { closeModalAction } from '../../../actions/modal';
import Avatar from '@mui/material/Avatar';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import ButtonScogoPrimaryOutlined from '../../../common/Buttons/ButtonScogoPrimaryOutlined';

export const ConnectMember = ({ members, chat_id }) => {
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const dispatch = useDispatch();
    const isConnectLoading = useSelector((state) => state.loading.isConnectLoading);

    // Filter members based on search query
    const filteredMembers = useMemo(() => {
        if (!searchQuery.trim()) return members;
        const query = searchQuery.toLowerCase();
        return members.filter(member =>
            member.display_name?.toLowerCase().includes(query)
        );
    }, [members, searchQuery]);

    const toggleSelection = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (selectedIds.length === filteredMembers.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredMembers.map(m => m.id));
        }
    };

    const handleSubmit = () => {
        dispatch(connectRoom({
            chat_id: chat_id,
            mentions: selectedIds.map(id => {
                return {
                    id,
                    display_name: members.find(m => m.id === id)?.display_name
                }
            })
        }));
    };

    const getInitials = (name) => {
        if (!name) return "?";
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    const allSelected = filteredMembers.length > 0 && selectedIds.length === filteredMembers.length;

    return (
        <div className="relative bg-white rounded-2xl shadow-2xl w-full mx-auto overflow-hidden">

            <div className="p-6">
                {/* Search Bar */}
                {members.length > 0 && (
                    <div className="mb-4">
                        <div className="relative">
                            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search members..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Select All */}
                {filteredMembers.length > 0 && (
                    <div className="mb-3 pb-3 border-b border-gray-200">
                        <label className="flex items-center cursor-pointer group">
                            <div className="relative">
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={handleSelectAll}
                                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer transition-all"
                                />
                            </div>
                            <span className="ml-3 text-sm font-semibold text-gray-700 group-hover:text-blue-600 transition-colors">
                                {allSelected ? 'Deselect All' : 'Select All'}
                            </span>
                            <span className="ml-auto text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                                {selectedIds.length} selected
                            </span>
                        </label>
                    </div>
                )}

                {/* Members List */}
                {members.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <p className="text-gray-500 font-medium">No members available</p>
                        <p className="text-gray-400 text-sm mt-1">There are no members to connect at this time</p>
                    </div>
                ) : filteredMembers.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <p className="text-gray-500 font-medium">No results found</p>
                        <p className="text-gray-400 text-sm mt-1">Try searching with different keywords</p>
                    </div>
                ) : (
                    <div className="max-h-[22rem] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 rounded-xl">
                        <ul className="space-y-1">
                            {filteredMembers.map((member, index) => (
                                <li
                                    key={member.id}
                                    className="group animate-fadeIn"
                                    style={{ animationDelay: `${index * 30}ms` }}
                                >
                                    <label
                                        htmlFor={member.id}
                                        className="flex items-center p-3 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 cursor-pointer border-2 border-transparent hover:border-blue-100"
                                    >
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                id={member.id}
                                                checked={selectedIds.includes(member.id)}
                                                onChange={() => toggleSelection(member.id)}
                                                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer transition-all"
                                            />
                                        </div>
                                        <div className="ml-4 flex items-center flex-1">
                                            <div className="relative">
                                                <Avatar
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        bgcolor: `hsl(210, 70%, 75%)`,
                                                        fontSize: '1rem',
                                                        fontWeight: 600,
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                        transition: 'all 0.2s',
                                                    }}
                                                    className="group-hover:scale-110"
                                                >
                                                    {getInitials(member.display_name)}
                                                </Avatar>
                                            </div>
                                            <div className="ml-4 flex-1">
                                                <p className="text-gray-800 font-semibold group-hover:text-blue-700 transition-colors">
                                                    {member.display_name}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {member.email || 'Member'}
                                                </p>
                                            </div>
                                        </div>
                                    </label>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Footer Actions */}
            {members.length > 0 && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center gap-3 justify-end">
                    <ButtonScogoPrimaryOutlined
                        textOrComponent="Cancel"
                        onClick={() => dispatch(closeModalAction())}
                        disabled={isConnectLoading}
                        loading={isConnectLoading}
                    />
                    <ButtonScogoPrimary
                        textOrComponent="Connect"
                        onClick={handleSubmit}
                        disabled={selectedIds.length === 0 || isConnectLoading}
                        loading={isConnectLoading}
                    />
                </div>
            )}

            <style jsx>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes scaleIn {
                    from {
                        transform: scale(0);
                    }
                    to {
                        transform: scale(1);
                    }
                }

                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out forwards;
                }

                .animate-scaleIn {
                    animation: scaleIn 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }

                .scrollbar-thin::-webkit-scrollbar {
                    width: 8px;
                }

                .scrollbar-thin::-webkit-scrollbar-track {
                    background: #f3f4f6;
                    border-radius: 10px;
                }

                .scrollbar-thin::-webkit-scrollbar-thumb {
                    background: #d1d5db;
                    border-radius: 10px;
                }

                .scrollbar-thin::-webkit-scrollbar-thumb:hover {
                    background: #9ca3af;
                }
            `}</style>
        </div>
    );
};
